<!--
  Aba Combos do cliente (NS 2026-0090): as compras, o saldo de cada uma (do razão,
  nunca somado aqui), o pagamento da OS da venda e o histórico com link para a OS.
  Fica montada dentro do ClienteModal: carrega em loadData(), ao abrir a aba.
-->
<template>
  <div class="cliente-combos" data-testid="cliente-combos-tab">
    <div v-if="erro" class="cc-aviso" data-testid="cliente-combos-erro">{{ erro }}</div>

    <div v-else-if="carregado && compras.length === 0" class="cc-aviso" data-testid="cliente-combos-vazio">
      {{ t('Este cliente ainda não comprou nenhum combo.') }}
    </div>

    <box
      v-for="compra in compras"
      :key="compra.compra_id"
      :title="compra.combo_nome"
    >
      <div class="cc-compra" :data-testid="`cliente-combo-${compra.compra_id}`">
        <div class="cc-tags">
          <status-tag :value="statusCompra(compra).label" :severity="statusCompra(compra).severity" />
          <status-tag
            :value="financialLabel(compra.situacao_financeira)"
            :severity="compra.situacao_financeira === 'recebido' ? 'success' : 'warn'"
          />
          <InfoTip :explanation="EX.pagamento" :label="t('Pagamento')" :test-id="`cliente-combo-${compra.compra_id}-pagamento-info`" />
        </div>

        <div class="cc-saldo">
          <StatsCard compact :label="t('Pares comprados')" :value="compra.quantidade" icon="pi pi-box" variant="info" :explanation="EX.pares" />
          <StatsCard compact :label="t('Disponível')" :value="compra.disponivel" icon="pi pi-check-circle" variant="success" :explanation="EX.disponivel" />
          <StatsCard compact :label="t('Reservado')" :value="compra.reservado" icon="pi pi-clock" variant="warning" :explanation="EX.reservado" />
          <StatsCard compact :label="t('Consumido')" :value="compra.consumido" icon="pi pi-history" variant="primary" :explanation="EX.consumido" />
        </div>

        <dl class="cc-dados">
          <div>
            <dt>{{ t('Preço') }}</dt>
            <dd>{{ formatarMoeda(compra.preco_total, compra.franquia_id) }}</dd>
          </div>
          <div>
            <dt>{{ t('Recebido na OS') }}</dt>
            <dd>{{ formatarMoeda(compra.venda_recebido, compra.franquia_id) }} / {{ formatarMoeda(compra.venda_total, compra.franquia_id) }}</dd>
          </div>
          <div>
            <dt>{{ t('Comprado em') }}</dt>
            <dd>{{ compra.created_at ? formatDate(compra.created_at) : '-' }}</dd>
          </div>
          <div>
            <dt>{{ t('Validade') }}</dt>
            <dd>{{ compra.expires_at ? formatDate(compra.expires_at) : t('Não vence') }}</dd>
          </div>
          <div>
            <dt>{{ t('OS da venda') }}</dt>
            <dd>
              <app-button
                :label="`#${compra.order_id}`"
                size="small"
                link
                no-explanation="link para a OS da venda"
                :data-testid="`cliente-combo-${compra.compra_id}-os`"
                @click="abrirOS(compra.order_id)"
              />
            </dd>
          </div>
          <div v-if="compra.status === 'cancelada'">
            <dt>{{ t('Motivo do cancelamento') }}</dt>
            <dd :data-testid="`cliente-combo-${compra.compra_id}-motivo`">{{ compra.motivo_cancelamento || '-' }}</dd>
          </div>
        </dl>

        <div v-if="podeCorrigir && compra.status === 'ativa'" class="cc-acoes">
          <app-button
            :label="t('Ajustar')"
            icon="pi pi-sliders-h"
            size="small"
            severity="secondary"
            :explanation="EX.ajustar"
            :data-testid="`cliente-combo-${compra.compra_id}-ajustar`"
            @click="abrirCorrecao('ajustar', compra)"
          />
          <app-button
            :label="t('Cancelar compra')"
            icon="pi pi-times"
            size="small"
            severity="danger"
            :explanation="EX.cancelarCompra"
            :data-testid="`cliente-combo-${compra.compra_id}-cancelar`"
            @click="abrirCorrecao('cancelar', compra)"
          />
        </div>

        <ComboMovimentosTable
          :test-id-prefix="`cliente-combo-${compra.compra_id}-movimentos`"
          :movimentos="compra.movimentos"
          :franchise-id="compra.franquia_id"
          :permite-estorno="podeCorrigir && compra.status === 'ativa'"
          @estornar="(movimento) => abrirCorrecao('estornar', compra, movimento.movimento_id)"
        />
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useClienteCombos, type CompraCombo } from '../../composables/useClienteCombos';
import { useMoedaFranquia } from '../../composables/useMoedaFranquia';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useUserPermissions } from '../../composables/usePermissions';
import StatsCard from '../../ui/StatsCard.vue';
import { comboExplanations } from '../so-combos/explanations';
import ComboMovimentosTable from '../so-combos/ComboMovimentosTable.vue';

const props = defineProps<{ clienteId: number }>();

const { t } = useI18n();
const { fetchCompras } = useClienteCombos();
const { formatarMoeda } = useMoedaFranquia();
const { formatDate } = useDateFormatter();
const { isManagerOrAbove: podeCorrigir } = useUserPermissions();

const EX = computed(() => comboExplanations(t));
const compras = ref<CompraCombo[]>([]);
const carregado = ref<boolean>(false);
const erro = ref<string | null>(null);

const statusCompra = (compra: CompraCombo): { label: string; severity: string } => {
  if (compra.status === 'cancelada') return { label: t('Cancelada'), severity: 'danger' };
  if (compra.vencida) return { label: t('Vencida'), severity: 'warn' };
  return { label: t('Ativa'), severity: 'success' };
};

const financialLabel = (situacao: string): string => {
  const rotulos: Record<string, string> = {
    a_receber: t('A receber'),
    recebimento_parcial: t('Parcial'),
    recebido: t('Recebido'),
  };
  return rotulos[situacao] ?? situacao;
};

const loadData = async (): Promise<void> => {
  erro.value = null;
  try {
    compras.value = await fetchCompras(props.clienteId);
  } catch (err) {
    compras.value = [];
    erro.value = err instanceof Error ? err.message : t('Erro ao carregar os combos do cliente');
  } finally {
    carregado.value = true;
  }
};

const abrirOS = (orderId: number): void => {
  eventBus.emit('modal.pdv-checkout.open', { orderId });
};

const abrirCorrecao = (acao: 'estornar' | 'ajustar' | 'cancelar', compra: CompraCombo, movimentoId?: number): void => {
  eventBus.emit('modal.cliente-combo-correcao.open', {
    acao,
    purchaseId: compra.compra_id,
    comboNome: compra.combo_nome,
    movimentoId,
  });
};

const onCorrigido = (): void => void loadData();

onMounted(() => {
  eventBus.on('cliente-combos.refresh', onCorrigido);
});

onBeforeUnmount(() => {
  eventBus.off('cliente-combos.refresh', onCorrigido);
});

defineExpose({ loadData });
</script>

<style scoped>
.cliente-combos {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cc-aviso {
  padding: 1rem;
}

.cc-compra {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cc-tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cc-saldo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.cc-dados {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  margin: 0;
}

.cc-dados dt {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

.cc-dados dd {
  margin: 0;
  font-weight: 600;
}

.cc-acoes {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
